<template>
  <div>
    <v-skeleton-loader v-if="loading" type="card, article" class="mx-auto my-4" />

    <div v-else>
      <v-sheet v-if="casefile.id" class="header-section px-6 py-4" dark>
        <v-row align="center">
          <v-col cols="12" sm="8">
            <div class="d-flex flex-wrap align-center">
                <h2 class="text-h5 font-weight-bold mb-0 text-break">
                  AA/{{ deptName }}{{ subRefCode }}/{{ casefile.id }}/{{ year }}{{ branchCode }}
                </h2>
              <v-chip small :color="getStatusColorClass" class="status-chip ml-3">
                <span class="status-text">{{ stageName || 'N/A' }}</span>
              </v-chip>
            </div>
            <div class="text-subtitle-1 mt-1 opacity-medium text-break">
              {{ insurerName || 'N/A' }}
              <span class="mx-2">•</span>
              {{ casefile.vehicleNo?.toUpperCase() }}
            </div>
          </v-col>
          <v-col cols="12" sm="4" class="text-right d-flex align-center justify-end flex-wrap">
            <TransferCase
              :approvingManager="approvingManager"
              :casefile="casefile"
              :refType="$store.state.referenceData.departments || []"
                :subRefTypes="normalizedSubRefTypes"
               :insurer="$store.state.referenceData.insurers || []"
               :branches="$store.state.referenceData.branches || []"
               :members="membersList"
               :states="$store.state.referenceData.states || []"
               class="mr-3"
             />
            <TransferAdj
              :casefile="casefile"
              :refType="$store.state.referenceData.departments || []"
              :branches="$store.state.referenceData.branches || []"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers || []"
              :management="management"
              :states="$store.state.referenceData.states || []"
               :regions="regions"
              :handlers="handlersList"
              :members="membersList"
              :adjusters="adjustersList"
              class="mr-3"
            />
            <TransferClerk
              ref="transferClerkRef"
              :branches="$store.state.referenceData.branches || []"
              :management="management"
              :insurer="$store.state.referenceData.insurers || []"
              :refType="$store.state.referenceData.departments || []"
              :subRefTypes="normalizedSubRefTypes"
              :members="membersList"
              :handlers="handlersList"
              :clerks="clerksList"
              :states="$store.state.referenceData.states || []"
              class="mr-3"
            />
            <CancelCase
              :casefile="casefile"
              :approvingManager="approvingManager"
              :refType="$store.state.referenceData.departments || []"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers || []"
              :management="management"
              class="mr-3"
            />
            <EditCase
              :casefile="casefile"
              :management="management"
              :refType="$store.state.referenceData.departments || []"
              :branches="$store.state.referenceData.branches || []"
               ref="editCaseRef"
               :regions="regions"
               :subRefTypes="normalizedSubRefTypes"
               :handlers="handlersList"
               :states="states.length ? states : $store.state.referenceData.states"
              :stages="$store.state.referenceData.stages || []"
              :insurer="$store.state.referenceData.insurers || []"
              class="mr-3"
            />
            <acknowledgement
              :casefile="casefile"
              :refType="$store.state.referenceData.departments || []"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers || []"
              :branches="$store.state.referenceData.branches || []"
              :states="$store.state.referenceData.states || []"
              class="mr-3"
            />
            <flag :casefile="casefile" class="d-inline-block flag-icon" />
          </v-col>
        </v-row>
      </v-sheet>

      <!-- Workflow Actions (partial - lazy-loaded action components inside) -->
      <v-card flat class="workflow-actions ma-4" v-if="casefile.id">
        <v-card-title class="workflow-title d-flex align-center pb-4">
          <v-icon large color="primary" class="mr-2">mdi-ray-start-arrow</v-icon>
          <span class="title-text">Available Actions</span>
          <v-spacer></v-spacer>
          <v-chip v-if="dateDiff && casefile.dateOfAssign" outlined small class="purple lighten-3 mr-3">
            {{ dateDiff(casefile.dateOfAssign, new Date()) }} Day(s)
          </v-chip>
        </v-card-title>

        <v-card-text class="action-buttons pa-6">
          <v-row justify="center" align="start">
            <!-- Render workflow action components conditionally (mirrors legacy behavior) -->
            <ApplyDoc
              v-if="hasAvailableActions.showApplyDoc"
              ref="applyDoc"
              :casefile="casefile"
              :refType="$store.state.referenceData.departments"
              :branches="$store.state.referenceData.branches"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers"
              :management="management"
              class="action-wrapper"
            />

            <AssignBranch
              v-if="hasAvailableActions.showAssignBranch"
              ref="assignBranch"
              :casefile="casefile"
              :refType="$store.state.referenceData.departments"
              :branches="$store.state.referenceData.branches"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers"
              :management="management"
              :states="states"
              :handlers="handlersList"
              :members="membersList"
              class="action-wrapper"
            />

            <BranchAccept
              v-if="hasAvailableActions.showBranchAccept"
              ref="branchAccept"
              :casefile="casefile"
              :refType="$store.state.referenceData.departments"
              :branches="$store.state.referenceData.branches"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers"
              :management="management"
              :states="states"
              :handlers="handlersList"
              :members="membersList"
              :adjusters="adjustersList"
              class="action-wrapper"
            />

            <AcceptAssignment
              v-if="hasAvailableActions.showAcceptAssignment"
              ref="acceptAssignment"
              :casefile="casefile"
              :refType="$store.state.referenceData.departments"
              :branches="$store.state.referenceData.branches"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers"
              :members="membersList"
              :handlers="handlersList"
              :states="states"
              @accepted="refreshCasefile"
              class="action-wrapper"
            />

            <CompleteInvestigation
              v-if="hasAvailableActions.showCompleteInvestigation"
              ref="completeInvestigation"
              :casefile="casefile"
              :claims="[]"
              :refType="$store.state.referenceData.departments"
              :branches="$store.state.referenceData.branches"
              :branch="casefile.branch"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers"
              :clerks="clerksList"
              :members="membersList"
              :documents="documents"
              @refresh-documents="handleDocumentUploaded"
              @refresh-casefile="refreshCasefile"
              class="action-wrapper"
            />

            <AssignEditor
              v-if="hasAvailableActions.showAssignEditor"
              ref="assignEditor"
              :casefile="casefile"
              :branches="$store.state.referenceData.branches"
              :branch="casefile.branch"
              :management="management"
              :editors="editorsList"
              :refType="$store.state.referenceData.departments"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers"
              :members="membersList"
              class="action-wrapper"
            />

            <AcceptEditing
              v-if="hasAvailableActions.showAcceptEditing"
              ref="acceptEditing"
              :casefile="casefile"
              class="action-wrapper"
            />

            <CompleteEditing
              v-if="hasAvailableActions.showCompleteEditing"
              ref="completeEditing"
              :casefile="casefile"
              :refType="$store.state.referenceData.departments"
              :branches="$store.state.referenceData.branches"
              :branch="casefile.branch"
              :subRefTypes="normalizedSubRefTypes"
              :insurer="$store.state.referenceData.insurers"
              :clerks="clerksList"
              :members="membersList"
              class="action-wrapper"
            />

            <HoldApproval
              v-if="hasAvailableActions.showHoldApproval"
              ref="holdApproval"
              :approvingManager="approvingManager"
              class="action-wrapper"
            />

            <ApprovalReport
              v-if="hasAvailableActions.showApprovalReport"
              ref="approvalReport"
              :holdingManager="approvingManager"
              class="action-wrapper"
            />

            <ReInvestigate
              v-if="hasAvailableActions.showReInvestigate"
              ref="reInvestigate"
              :holdingManager="approvingManager"
              class="action-wrapper"
            />

            <ReEdit
              v-if="hasAvailableActions.showReEdit"
              ref="reEdit"
              :holdingManager="approvingManager"
              class="action-wrapper"
            />

            <AssignClerk
              v-if="hasAvailableActions.showAssignClerk"
              ref="assignClerk"
              :casefile="casefile"
              :branches="$store.state.referenceData.branches"
              :branch="actionBranch"
              :refType="$store.state.referenceData.departments"
              :subRefTypes="normalizedSubRefTypes"
               :insurer="$store.state.referenceData.insurers"
               :members="membersList"
               :management="management"
               :clerks="clerksList"
              :documents="documents"
              @refresh-documents="handleDocumentUploaded"
              class="action-wrapper"
            />

            <AcceptFormatting
              v-if="hasAvailableActions.showAcceptFormatting"
              ref="acceptFormatting"
              :casefile="casefile"
              class="action-wrapper"
            />

            <CompleteFormatting
              v-if="hasAvailableActions.showCompleteFormatting"
              ref="completeFormatting"
              :casefile="casefile"
              class="action-wrapper"
            />

            <Invoising
              v-if="hasAvailableActions.showInvoising"
              ref="invoising"
              :management="management"
              class="action-wrapper"
            />

            <Dispatch
              v-if="hasAvailableActions.showDispatch"
              ref="dispatch"
              :management="management"
              class="action-wrapper"
            />

            <DoneDispatch
              v-if="hasAvailableActions.showDoneDispatch"
              ref="doneDispatch"
              :management="management"
              class="action-wrapper"
            />

            <CloseAction
              v-if="hasAvailableActions.showClose"
              ref="close"
              :management="management"
              class="action-wrapper"
            />

            <!-- No Actions Message -->
            <v-col cols="12" v-if="!Object.values(hasAvailableActions).some((v) => v)">
              <div class="no-actions text-center pa-6">
                <v-icon size="48" color="grey lighten-1">mdi-timer-sand</v-icon>
                <h3 class="mt-4 grey--text text--darken-1">Awaiting Next Action</h3>
                <p class="subtitle-1 grey--text">Current Stage: {{ stageName || 'N/A' }}</p>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-tabs v-model="tab" class="mt-4" grow>
        <v-tab>Details</v-tab>
        <v-tab>
          Documents
          <template v-if="documentsCompLoading || documentsLoading">
            <v-progress-circular indeterminate size="16" width="2" color="success" class="ml-2" />
          </template>
          <v-badge v-else :content="documentsLoaded ? (documentCount || '0') : '?'" color="success" offset-x="5" offset-y="1" />
        </v-tab>
        <v-tab>
          Comments
          <template v-if="commentsCompLoading || commentsLoading">
            <v-progress-circular indeterminate size="16" width="2" color="warning" class="ml-2" />
          </template>
          <v-badge v-else :content="commentsLoaded ? (commentCount || '0') : '?'" color="warning" offset-x="5" offset-y="1" />
        </v-tab>
        <v-tab>
          History
          <template v-if="historyCompLoading || historyLoading">
            <v-progress-circular indeterminate size="16" width="2" color="info" class="ml-2" />
          </template>
          <v-badge v-else :content="historyLoaded ? (historyTabCount || '0') : '?'" color="info" offset-x="5" offset-y="1" />
        </v-tab>
        <v-tab>
          Audit
          <template v-if="auditCompLoading">
            <v-progress-circular indeterminate size="16" width="2" color="primary" class="ml-2" />
          </template>
        </v-tab>
        <v-tab>
          Claims
          <template v-if="claimsCompLoading || claimsLoading">
            <v-progress-circular indeterminate size="16" width="2" color="primary" class="ml-2" />
          </template>
          <v-badge v-else :content="claimsLoaded ? (claimCount || '0') : '?'" color="primary" offset-x="5" offset-y="1" />
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item>
          <DetailTab
            :adjusters="adjustersList"
            :members="membersList"
            :editors="editorsList"
            :clerks="clerksList"
            :branches="$store.state.referenceData.branches"
            :insurer="$store.state.referenceData.insurers"
            :handlers="handlersList"
            :refType="$store.state.referenceData.departments"
            :subRefTypes="normalizedSubRefTypes"
            :stages="$store.state.referenceData.stages"
            :states="states.length ? states : $store.state.referenceData.states"
            :casefile="casefile"
          />
        </v-tab-item>
        <v-tab-item>
          <div>
            <div v-if="tab === 1 && (documentsCompLoading || documentsLoading)" class="tab-skeleton-wrapper">
              <div class="tab-skeleton" style="min-height:180px">
                <v-skeleton-loader type="card, list-item-two-line" class="mx-2 my-4" />
              </div>
            </div>
            <div v-else-if="tab === 1 && documentsCompError">
              <v-alert type="error" dense>
                Failed to load Documents. <v-btn text small @click="loadChildComponents">Retry</v-btn>
              </v-alert>
            </div>
            <component
              :is="documentsComp"
              v-else-if="tab === 1 && documentsComp"
              :casefile="casefile"
              :preloadedDocs="documents"
              :claimTotal="claimTotal"
              @document-uploaded="handleDocumentUploaded"
            />
          </div>
        </v-tab-item>
        <v-tab-item>
          <div>
            <div v-if="tab === 2 && (commentsCompLoading || commentsLoading)" class="tab-skeleton-wrapper">
              <div class="tab-skeleton" style="min-height:180px">
                <v-skeleton-loader type="card, list-item-two-line" class="mx-2 my-4" />
              </div>
            </div>
            <div v-else-if="tab === 2 && commentsCompError">
              <v-alert type="error" dense>
                Failed to load Comments. <v-btn text small @click="loadChildComponents">Retry</v-btn>
              </v-alert>
            </div>
            <component
              :is="commentsComp"
              v-else-if="tab === 2 && commentsComp"
              :casefile="casefile"
              :preloadedComments="comments"
              @comment-added="handleCommentAdded"
              @comment-deleted="handleCommentDeleted"
            />
          </div>
        </v-tab-item>
        <v-tab-item>
          <div>
            <div v-if="tab === 3 && (historyCompLoading || historyLoading)" class="tab-skeleton-wrapper">
              <div class="tab-skeleton" style="min-height:180px">
                <v-skeleton-loader type="card, list-item-two-line" class="mx-2 my-4" />
              </div>
            </div>
            <div v-else-if="tab === 3 && historyCompError">
              <v-alert type="error" dense>
                Failed to load History. <v-btn text small @click="loadChildComponents">Retry</v-btn>
              </v-alert>
            </div>
            <component
              :is="historyComp"
              v-else-if="tab === 3 && historyComp"
              :casefile="casefile"
              :preloadedHistory="history"
              :branches="$store.state.referenceData.branches"
              :states="states.length ? states : $store.state.referenceData.states"
                :insurer="$store.state.referenceData.insurers"
                :handlers="handlers.length ? handlers : handlersList"
                :refType="$store.state.referenceData.departments"
                :subRefTypes="normalizedSubRefTypes"
                :members="membersList"
                :stages="$store.state.referenceData.stages"
              @history-count-updated="handleHistoryCountUpdated"
            />
          </div>
        </v-tab-item>
        <v-tab-item>
          <div>
            <div v-if="tab === 4 && auditCompLoading" class="tab-skeleton-wrapper">
              <div class="tab-skeleton" style="min-height:180px">
                <v-skeleton-loader type="card, list-item-two-line" class="mx-2 my-4" />
              </div>
            </div>
            <div v-else-if="tab === 4 && auditCompError">
              <v-alert type="error" dense>
                Failed to load Audit. <v-btn text small @click="loadChildComponents">Retry</v-btn>
              </v-alert>
            </div>
            <component :is="auditComp" v-else-if="tab === 4 && auditComp" :members="membersList" />
          </div>
        </v-tab-item>
        <v-tab-item>
          <div>
            <div v-if="tab === 5 && claimsCompLoading" class="tab-skeleton-wrapper">
              <div class="tab-skeleton" style="min-height:180px">
                <v-skeleton-loader type="card, list-item-two-line" class="mx-2 my-4" />
              </div>
            </div>
            <div v-else-if="tab === 5 && claimsCompError">
              <v-alert type="error" dense>
                Failed to load Claims. <v-btn text small @click="loadChildComponents">Retry</v-btn>
              </v-alert>
            </div>
            <component :is="claimsComp" v-else-if="tab === 5 && claimsComp" :branches="$store.state.referenceData.branches" :refType="$store.state.referenceData.departments" :subRefTypes="normalizedSubRefTypes" :members="membersList" :preloadedClaims="claims" />
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import DocsDataService from "@/services/DocsDataService";
import MemberDataService from "@/services/MemberDataService";
// Lazy-load heavy action components to keep initial bundle small

export default {
  name: "detailFast",
    components: {
    TransferCase: () => import("../../components/detailView/transferCase.vue"),
    TransferAdj: () => import("../../components/detailView/transferAdj.vue"),
    TransferClerk: () => import("../../components/detailView/transferClerk.vue"),
    CancelCase: () => import("../../components/detailView/cancelCase.vue"),
    EditCase: () => import("../../components/detailView/editCase.vue"),
    acknowledgement: () => import("../../components/pdf/acknowledgement.vue"),
    flag: () => import("../../components/flag/flag"),
    // Workflow action components (lazy)
    ApplyDoc: () => import("../../components/detailView/applyDoc.vue"),
    AssignBranch: () => import("../../components/detailView/assignBranch.vue"),
    BranchAccept: () => import("../../components/detailView/branchAccept.vue"),
    CompleteInvestigation: () => import("../../components/detailView/completeInvestigation.vue"),
    AssignEditor: () => import("../../components/detailView/assignEditor.vue"),
    AcceptEditing: () => import("../../components/detailView/acceptEditing.vue"),
    CompleteEditing: () => import("../../components/detailView/completeEditing.vue"),
    HoldApproval: () => import("../../components/detailView/holdApproval.vue"),
    ApprovalReport: () => import("../../components/detailView/approvalReport.vue"),
    ReInvestigate: () => import("../../components/detailView/reInvestigate.vue"),
    ReEdit: () => import("../../components/detailView/reEdit.vue"),
    AssignClerk: () => import("../../components/detailView/assignClerk.vue"),
    AcceptFormatting: () => import("../../components/detailView/acceptFormatting.vue"),
    CompleteFormatting: () => import("../../components/detailView/completeFormatting.vue"),
    Invoising: () => import("../../components/detailView/invoising.vue"),
    Dispatch: () => import("../../components/detailView/dispatch.vue"),
    DoneDispatch: () => import("../../components/detailView/doneDispatch.vue"),
    CloseAction: () => import("../../components/detailView/close.vue"),
    AcceptAssignment: () => import("../../components/detailView/acceptAssignment.vue"),
    DetailTab: () => import("../../components/detailView/detailTab.vue"),
  },
    data() {
      return {
      loading: true,
      tab: 0,
      documentsComp: null,
      documentsCompLoading: false,
      documentsCompError: false,
      commentsComp: null,
      commentsCompLoading: false,
      commentsCompError: false,
      historyComp: null,
      historyCompLoading: false,
      historyCompError: false,
      auditComp: null,
      auditCompLoading: false,
      auditCompError: false,
      claimsComp: null,
      claimsCompLoading: false,
      claimsCompError: false,
      casefile: {},
      // Local mutable branch object used by action components that expect an object
      // with { name, brCode } (matches legacy vuexFile branch shape).
      actionBranch: { name: "", brCode: "" },
      // local cached role lists (populate like vuexFile for immediate card rendering)
      localMembers: [],
      localAdjusters: [],
      localEditors: [],
      localClerks: [],
      documents: [],
      comments: [],
      history: [],
      claims: [],
      states: [],
      regions: [],
      subRefTypes: [],
      handlers: [],
      // Reactive flags and counts used by template and child components
      documentsLoading: false,
      documentCount: 0,
      commentsLoading: false,
      commentCount: 0,
      historyLoading: false,
      historyCount: 0,
      // Track whether we've already loaded each tab's data to avoid refetching
      documentsLoaded: false,
      commentsLoaded: false,
      historyLoaded: false,
      // Claims tab flags
      claimsLoaded: false,
      claimsLoading: false,
      claimCount: 0,
      // internal flags for runtime patches
      transferClerkPatched: false,
      origTransferClerkClose: null,
      ignoreStoreAssignment: false,
    };
  },
    computed: {
    ...mapState(["caseState"]),
    // Pre-filtered member lists to pass to action components (reduces child logic)
    membersList() {
      return this.$store.state.referenceData.members || [];
    },
    adjustersList() {
      // Prefer locally-cached adjusters (from MemberDataService) if available
      if (this.localAdjusters && this.localAdjusters.length) return this.localAdjusters;
      const list = this.membersList;
      return list.filter((m) => {
        const roles = Array.isArray(m.roles)
          ? m.roles
          : m.role
          ? [m.role]
          : m.roleCode
          ? [m.roleCode]
          : [];
        return roles.some((r) => String(r).toUpperCase() === "ADJUSTER");
      });
    },
    clerksList() {
      if (this.localClerks && this.localClerks.length) return this.localClerks;
      const list = this.membersList;
      return list.filter((m) => {
        const roles = Array.isArray(m.roles)
          ? m.roles
          : m.role
          ? [m.role]
          : m.roleCode
          ? [m.roleCode]
          : [];
        return roles.some((r) => ["CLERK", "BRANCHCLERK"].includes(String(r).toUpperCase()));
      });
    },
    editorsList() {
      if (this.localEditors && this.localEditors.length) return this.localEditors;
      const list = this.membersList;
      return list.filter((m) => {
        const roles = Array.isArray(m.roles)
          ? m.roles
          : m.role
          ? [m.role]
          : m.roleCode
          ? [m.roleCode]
          : [];
        return roles.some((r) => ["EDITOR", "MANAGER", "BRANCHMANAGER", "BRANCHCLERK", "CLERK"].includes(String(r).toUpperCase()));
      });
    },
    handlersList() {
      // Prefer preloaded handlers (from HandlerDataService) else fall back to member list
      return this.handlers && this.handlers.length ? this.handlers : this.membersList;
    },
    deptName() {
      const d = this.$store.getters["referenceData/deptById"](
        this.casefile.refType
      );
      return d ? d.name : "";
    },
    insurerName() {
      const i = this.$store.getters["referenceData/insurerById"](
        this.casefile.insurer
      );
      return i ? i.name : "";
    },
    stageName() {
      const s = this.$store.getters["referenceData/stageByCode"](
        this.casefile.fileStatus
      );
      return s ? s.name : null;
    },
    year() {
      return this.casefile.createdAt ? moment(this.casefile.createdAt).format("YY") : "";
    },
    // Derived small helpers used in header
    getStatusColorClass() {
      const status = this.casefile.fileStatus;
      const colors = {
        NEW: "success",
        PEND: "warning",
        INVE: "info",
        COMP: "error",
        CLOSED: "grey",
      };
      return colors[status] || "grey";
    },
    subRefCode() {
      if (!this.casefile.subRefType) return "";
      // Prefer local preloaded subRefTypes (cascaded fetch) then fallback to store
      const localList = this.subRefTypes || [];
      let sub = localList.find((s) => s.id === this.casefile.subRefType);
      if (!sub) {
        const storeList = this.$store.state.referenceData.subRefTypes || [];
        sub = storeList.find((s) => s.id === this.casefile.subRefType);
      }
      return sub && sub.subCode ? "/" + sub.subCode : "";
    },
    // Provide a normalized subRefTypes list for children: prefer locally preloaded list
    // (fetched via SubDeptDataService in mounted) else fall back to store refs.
    normalizedSubRefTypes() {
      return this.subRefTypes && this.subRefTypes.length
        ? this.subRefTypes
        : this.$store.state.referenceData.subRefTypes || [];
    },
    branchCode() {
      if (!this.casefile.branch) return "";
      const b = (this.$store.state.referenceData.branches || []).find((br) => br.id === this.casefile.branch);
      return b && b.brCode ? "/" + b.brCode : "";
    },
    approvingManager() {
      return (
        this.$store.state.auth.user.roles.includes("ADMIN") ||
        this.$store.state.auth.user.roles.includes("MANAGER") ||
        this.$store.state.auth.user.roles.includes("BRANCHMANAGER")
      );
    },
    management() {
      const roles = this.$store.state.auth.user.roles || [];
      return (
        roles.includes("ADMIN") ||
        roles.includes("MANAGER") ||
        roles.includes("BRANCHMANAGER") ||
        roles.includes("BRANCHCLERK") ||
        roles.includes("CLERK")
      );
    },
    historyTabCount() {
      const vno = this.casefile?.vehicleNo;
      if (!vno) return 0;
      const val = String(vno).trim().toUpperCase();
      return val === "TBA" || val === "NONE" ? 0 : this.historyCount;
    },
    hasAvailableActions() {
      const status = this.casefile.fileStatus;
      const isAdjuster = this.casefile.adjuster === this.$store.state.auth.user.id;
      const isEditor = this.casefile.editor === this.$store.state.auth.user.id;
      const isClerk = this.casefile.clerkInCharge === this.$store.state.auth.user.id;

      return {
        showApplyDoc: status === "NEW" && this.management,
        showAssignBranch: (status === "NEW" || status === "PEND") && this.management,
        showBranchAccept: status === "BRAC" && this.management,

        showAcceptAssignment: status === "ADAC" && isAdjuster,
        showCompleteInvestigation: status === "INVE" && isAdjuster,

        showAssignEditor: (status === "PEDI" || status === "REIN") && this.management,
        showAcceptEditing: status === "EDAC" && isEditor,
        showCompleteEditing: status === "EDI" && isEditor,

        showHoldApproval: status === "PAPP" && this.approvingManager,
        showApprovalReport: status === "HOLD" && this.approvingManager,
        showReInvestigate: status === "HOLD" && this.approvingManager,
        showReEdit: status === "HOLD" && this.approvingManager,

        showAssignClerk: (status === "PFORM" || status === "PEDI") && this.management,
        showAcceptFormatting: status === "CLERK" && isClerk,
        showCompleteFormatting: status === "FOIN" && isClerk,

        showInvoising: status === "INFI" && this.management,
        showDispatch: status === "DISP" && this.management,
        showDoneDispatch: status === "PDISP" && this.management,
        showClose: status === "DISP" && this.management,
      };
    },
    claimTotal() {
      if (!Array.isArray(this.claims) || this.claims.length === 0) return 0;
      const total = this.claims.reduce((sum, c) => {
        return sum +
          (parseFloat(c.travMileage) || 0) +
          (parseFloat(c.additional) || 0) +
          (parseFloat(c.sdAndStamps) || 0) +
          (parseFloat(c.medicalReport) || 0) +
          (parseFloat(c.policeDoc) || 0) +
          (parseFloat(c.jpjDoc) || 0) +
          (parseFloat(c.misc) || 0) +
          (parseFloat(c.typing) || 0);
      }, 0);
      return total.toFixed(2);
    },
  },
  methods: {
    async loadChildComponents() {
      // lazy load when needed
      if (this.tab === 1 && !this.documentsComp) {
        this.documentsCompLoading = true;
        this.documentsCompError = false;
        // allow DOM to update so skeleton is visible before starting heavy import
        await this.$nextTick();
        try {
          const m = await import("./../../components/uploads/documents.vue");
          this.documentsComp = m.default || m;
        } catch (err) {
          console.error("Failed to load documents component", err);
          this.documentsCompError = true;
        } finally {
          this.documentsCompLoading = false;
        }
        // Load documents data on first activation of Documents tab
        if (!this.documentsLoaded) {
          this.documentsLoaded = true;
          this.documentsLoading = true;
          this.handleDocumentUploaded();
        }
        // Preload claims data so claim total is available for notification
        if (!this.claimsLoaded) {
          this.claimsLoaded = true;
          this.claimsLoading = true;
          this.handleClaimsLoaded();
        }
      }
      if (this.tab === 2 && !this.commentsComp) {
        this.commentsCompLoading = true;
        this.commentsCompError = false;
        await this.$nextTick();
        try {
          const m = await import("./../../components/comment/comments.vue");
          this.commentsComp = m.default || m;
        } catch (err) {
          console.error("Failed to load comments component", err);
          this.commentsCompError = true;
        } finally {
          this.commentsCompLoading = false;
        }
        // Load comments data on first activation of Comments tab
        if (!this.commentsLoaded) {
          this.commentsLoaded = true;
          // set commentsLoading early so skeleton and header spinner stay visible
          this.commentsLoading = true;
          this.handleCommentAdded();
        }
      }
      if (this.tab === 3 && !this.historyComp) {
        this.historyCompLoading = true;
        this.historyCompError = false;
        await this.$nextTick();
        try {
          const m = await import("./../../components/detailView/history.vue");
          this.historyComp = m.default || m;
        } catch (err) {
          console.error("Failed to load history component", err);
          this.historyCompError = true;
        } finally {
          this.historyCompLoading = false;
        }
        // Load history data on first activation of History tab
        if (!this.historyLoaded) {
          this.historyLoaded = true;
          // set historyLoading early so skeleton and header spinner stay visible
          this.historyLoading = true;
          this.preloadHistory();
        }
      }
      if (this.tab === 4 && !this.auditComp) {
        this.auditCompLoading = true;
        this.auditCompError = false;
        await this.$nextTick();
        try {
          const m = await import("./../../components/detailView/audit.vue");
          this.auditComp = m.default || m;
        } catch (err) {
          console.error("Failed to load audit component", err);
          this.auditCompError = true;
        } finally {
          this.auditCompLoading = false;
        }
      }
      if (this.tab === 5 && !this.claimsComp) {
        this.claimsCompLoading = true;
        this.claimsCompError = false;
        await this.$nextTick();
        try {
          const m = await import("./../../components/detailView/detailClaims.vue");
          this.claimsComp = m.default || m;
        } catch (err) {
          console.error("Failed to load claims component", err);
          this.claimsCompError = true;
        } finally {
          this.claimsCompLoading = false;
        }
        // load claims data on first activation
        if (!this.claimsLoaded) {
          this.claimsLoaded = true;
          this.claimsLoading = true;
          this.handleClaimsLoaded();
        }
      }
    },
    async handleDocumentUploaded() {
      // reload documents and update count
      this.documentsLoading = true;
      try {
        const res = await DocsDataService.findByCase(this.$route.params.id);
        const allDocs = Array.isArray(res.data) ? res.data : res.data.data || [];
        this.documents = allDocs.filter((doc) => doc.caseId === this.casefile.id);
        this.documentCount = this.documents.length;
      } catch (e) {
        console.error("detailFast: failed to reload documents", e);
      } finally {
        this.documentsLoading = false;
      }
    },

    async handleClaimsLoaded() {
      this.claimsLoading = true;
      try {
        const ClaimDataService = await import("@/services/ClaimDataService");
        const res = await ClaimDataService.default.getAll();
        const all = Array.isArray(res.data) ? res.data : res.data.data || [];
        const related = all.filter((c) => String(c.caseId) === String(this.casefile.id));
        this.claims = related; // cache full claims list in parent so child can reuse
        this.claimCount = related.length;
      } catch (e) {
        console.error("detailFast: failed to load claims count", e);
        this.claimCount = 0;
      } finally {
        this.claimsLoading = false;
      }
    },
    dateDiff(startDate, endDate) {
      if (!startDate || !endDate) return "";
      const start = moment(startDate);
      const end = moment(endDate);
      const duration = moment.duration(end.diff(start));
      return Math.floor(duration.asDays());
    },
    async handleCommentAdded() {
      // reload comments and update count (newComment may be provided by child)
      this.commentsLoading = true;
      try {
        const CommentsDataService = await import("@/services/CommentDataService");
        const cres = await CommentsDataService.default.findByCase(this.$route.params.id);
        const allComments = Array.isArray(cres.data) ? cres.data : cres.data.data || [];
        this.comments = allComments.filter((c) => c.caseId === this.casefile.id);
        this.commentCount = this.comments.length;
      } catch (e) {
        console.error("detailFast: failed to reload comments", e);
      } finally {
        this.commentsLoading = false;
      }
    },
    async handleCommentDeleted() {
      // reload comments and update count
      await this.handleCommentAdded();
    },
    handleHistoryCountUpdated(count) {
      // history component may emit an updated count (already filtered)
      this.historyCount = count;
    },
    async refreshCasefile() {
      // Re-fetch the single casefile via the existing paginated permitted endpoint
      try {
        await this.$store.dispatch("casefiles/fetchPaginatedPermitted", {
          page: 1,
          pageSize: 1,
          filters: { id: this.$route.params.id },
        });
        const list = this.$store.getters.getPaginatedPermitted;
        if (Array.isArray(list) && list.length > 0) {
          this.casefile = list[0];
          // keep a mutable branch object in sync for action components (AssignClerk expects branch.brCode)
          try {
            const branches = this.$store.state.referenceData.branches || [];
            const br = branches.find((b) => b && String(b.id) === String(this.casefile.branch));
            this.actionBranch = br ? { name: br.name || "", brCode: br.brCode || "" } : { name: "", brCode: "" };
          } catch (e) {
            this.actionBranch = { name: "", brCode: "" };
          }
          try {
            this.caseState.casefile = list[0];
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        console.error("detailFast: failed to refresh casefile", e);
      }
    },
    async preloadHistory() {
      // Load history in background for the current casefile (by vehicleNo)
      if (!this.casefile || !this.casefile.vehicleNo) {
        this.history = [];
        this.historyCount = 0;
        return;
      }
      this.historyLoading = true;
      try {
        const CasefileDataService = await import("@/services/CasefileDataService");
        const vehicleNo = this.casefile.vehicleNo;
        if (vehicleNo && String(vehicleNo).trim().toUpperCase() !== "TBA" && String(vehicleNo).trim().toUpperCase() !== "NONE") {
          const paramsFast = { page: 1, pageSize: 50, vehicleNo, vehicleNoExact: 'true', needCount: false };
          const respFast = await CasefileDataService.default.getPaginated(paramsFast);
          const payloadFast = respFast && respFast.data !== undefined ? respFast.data : respFast;
          const rowsFast = Array.isArray(payloadFast.data) ? payloadFast.data : Array.isArray(payloadFast.rows) ? payloadFast.rows : Array.isArray(payloadFast) ? payloadFast : [];
          const matched = rowsFast.filter((item) => String(item.vehicleNo) === String(vehicleNo));
          this.history = matched.filter((c) => c.id !== this.casefile.id);
          this.historyCount = this.history.length;
        } else {
          this.history = [];
          this.historyCount = 0;
        }
      } catch (e) {
        console.error("detailFast: failed to load history", e);
        this.history = [];
        this.historyCount = 0;
      } finally {
        this.historyLoading = false;
      }
    },

    async preloadAdditionalRefs() {
      // Load auxiliary reference lists (states, handlers) in background
      try {
        const [StateDataService, HandlerDataService, RegionDataService] = await Promise.all([
          import("@/services/StateDataService"),
          import("@/services/HandlerDataService"),
          import("@/services/RegionDataService"),
        ]);
        const [statesRes, handlersRes, regionsRes] = await Promise.all([
          StateDataService.default.getAll(),
          HandlerDataService.default.getAll(),
          RegionDataService.default.getAll(),
        ]);
        this.states = Array.isArray(statesRes.data) ? statesRes.data : statesRes.data.data || [];
        this.handlers = Array.isArray(handlersRes.data) ? handlersRes.data : handlersRes.data.data || [];
        this.regions = Array.isArray(regionsRes.data) ? regionsRes.data : regionsRes.data.data || [];
      } catch (e) {
        console.error("detailFast: failed to load additional refs", e);
        this.states = this.states || [];
        this.handlers = this.handlers || [];
      }
      // also preload member roles used for immediate card rendering
      this.preloadMemberRoles();
    },

    async preloadMemberRoles() {
      try {
        const resp = await MemberDataService.getAll();
        const members = Array.isArray(resp.data) ? resp.data : resp.data.data || [];
        this.localMembers = members.filter((m) => m.active);
        this.localAdjusters = members.filter((item) => item.roles && item.roles.some((b) => (b.roleCode || '').toLowerCase() === 'adjuster') && item.active);
        this.localEditors = members.filter((item) => item.roles && item.roles.some((b) => {
          const rc = (b.roleCode || '').toLowerCase();
          return ['editor','manager','branchmanager'].includes(rc);
        }) && item.active);
        this.localClerks = members.filter((item) => item.roles && item.roles.some((b) => {
          const rc = (b.roleCode || '').toLowerCase();
          return ['clerk','manager','branchclerk','branchmanager'].includes(rc);
        }) && item.active);
      } catch (e) {
        console.error('detailFast: failed to preload member roles', e);
        this.localMembers = [];
        this.localAdjusters = [];
        this.localEditors = [];
        this.localClerks = [];
      }
    },
  },
  watch: {
    tab() {
      this.loadChildComponents();
    },
    // Keep local casefile in sync when global caseState.casefile is mutated
    'caseState.casefile': {
      immediate: false,
      handler(newVal) {
        if (!newVal) {
          // If global casefile became null/undefined, reset local copy to empty
          // object so child components won't attempt to read properties off null.
          this.casefile = {};
          this.actionBranch = { name: "", brCode: "" };
          return;
        }
        try {
          // shallow copy to local casefile so template bindings update predictably
          this.casefile = Object.assign({}, newVal);
          // Normalize placeOfLoss to uppercase so child selects (which uppercases
          // region names) match the existing value when Edit dialog clones it.
          if (this.casefile && this.casefile.placeOfLoss && typeof this.casefile.placeOfLoss === 'string') {
            try {
              const up = this.casefile.placeOfLoss.trim().toUpperCase();
              this.casefile.placeOfLoss = up;
              // also attempt to normalize global caseState copy so children that
              // clone from it receive the normalized value
              if (this.caseState && this.caseState.casefile) this.caseState.casefile.placeOfLoss = up;
            } catch (e) { /* ignore */ }
          }
          // also keep actionBranch synced when branch changes elsewhere
          const branches = this.$store.state.referenceData.branches || [];
          const br = branches.find((b) => b && String(b.id) === String(this.casefile.branch));
          this.actionBranch = br ? { name: br.name || "", brCode: br.brCode || "" } : { name: "", brCode: "" };
        } catch (e) {
          // ignore sync errors
        }
      },
      deep: true,
    },
    // Ensure we react to fileStatus changes specifically and force a small sync so
    // the Available Actions computed values update immediately after mutations
    'caseState.casefile.fileStatus': function (newVal, oldVal) {
      if (newVal === oldVal) return;
      try {
        this.casefile = Object.assign({}, this.caseState.casefile || {});
        const branches = this.$store.state.referenceData.branches || [];
        const br = branches.find((b) => b && String(b.id) === String(this.casefile.branch));
        this.actionBranch = br ? { name: br.name || "", brCode: br.brCode || "" } : { name: "", brCode: "" };
        // Force update to ensure computed properties re-evaluate
        this.$nextTick(() => {});
      } catch (e) {
        // ignore
      }
    },
    'caseState.casefile.branch': function (newVal, oldVal) {
      if (newVal === oldVal) return;
      try {
        this.casefile = Object.assign({}, this.caseState.casefile || {});
        const branches = this.$store.state.referenceData.branches || [];
        const br = branches.find((b) => b && String(b.id) === String(newVal));
        this.actionBranch = br ? { name: br.name || "", brCode: br.brCode || "" } : { name: "", brCode: "" };
        this.$nextTick(() => {});
      } catch (e) {
        // ignore
      }
    },
    'caseState.casefile.clerkInCharge': function (newVal, oldVal) {
      if (newVal === oldVal) return;
      try {
        this.casefile = Object.assign({}, this.caseState.casefile || {});
        this.$nextTick(() => {});
      } catch (e) {
        // ignore
      }
    },
    'caseState.casefile.editor': function (newVal, oldVal) {
      if (newVal === oldVal) return;
      try {
        this.casefile = Object.assign({}, this.caseState.casefile || {});
        this.$nextTick(() => {});
      } catch (e) {
        // ignore
      }
    },
  },
    async mounted() {
    // If the feature flag is disabled at runtime, redirect back to the legacy page
    // Quick test override: add ?forceFast=1 to the URL to bypass the flag for testing
    const enabled = this.$store.getters["settings/isFileFastEnabled"];
    const forceFast = String(this.$route.query.forceFast || "") === "1";
    if (!enabled && !forceFast) {
      // fallback to legacy route
      this.$router.replace({ name: "vuexFile", params: { id: this.$route.params.id } });
      return;
    }

    // ensure reference data cached
    await this.$store.dispatch("referenceData/ensureRefs");

    // fetch sub-ref types early to ensure subRefCode (e.g. LCH) is available for header
    try {
      const SubRefTypeDataService = await import("@/services/SubDeptDataService");
      const subRefRes = await SubRefTypeDataService.default.getAll();
      this.subRefTypes = Array.isArray(subRefRes.data) ? subRefRes.data : subRefRes.data.data || [];
    } catch (e) {
      // ignore; we'll fallback to store referenceData later
      this.subRefTypes = [];
    }

    // fetch the casefile using existing paginated permitted endpoint
    await this.$store.dispatch("casefiles/fetchPaginatedPermitted", {
      page: 1,
      pageSize: 1,
      filters: { id: this.$route.params.id },
    });

    const list = this.$store.getters.getPaginatedPermitted;
        if (Array.isArray(list) && list.length > 0) {
          this.casefile = list[0];
          // keep global store caseState in sync for children that use it
          try {
            // Normalize placeOfLoss so EditCase sees matching item values
            if (list[0] && list[0].placeOfLoss && typeof list[0].placeOfLoss === 'string') {
              list[0].placeOfLoss = list[0].placeOfLoss.trim().toUpperCase();
            }
            this.caseState.casefile = list[0];
          } catch (e) {
            // ignore if mutation not permitted
          }
        }

    // Render the page immediately; start counts/loading for badges in background
    this.loading = false;

    // Kick off background loads for light-weight refs only; defer heavy lists until tab activation
    // start additional refs in background (don't await)
    this.preloadAdditionalRefs();

    // Watch the store's casefile fileStatus for changes that may be made by child
    // components directly (some legacy components assign to caseState.casefile
    // directly). Use $store.watch to ensure we react even when mutations are not
    // properly committed via Vuex mutations.
    // Debounced watcher to avoid racing with child components that mutate
    // caseState.casefile and then perform follow-up work. Delay refresh to
    // let child code finish synchronous post-update logic (emails, local
    // mutations) before we re-fetch from the server/store.
    this.refreshTimer = null;
    this.unwatchCaseStatus = this.$store.watch(
      (state) => state.caseState && state.caseState.casefile && state.caseState.casefile.fileStatus,
      (newVal, oldVal) => {
        if (newVal === oldVal) return;
        try {
          if (this.refreshTimer) clearTimeout(this.refreshTimer);
        } catch (e) { void 0; }
        this.refreshTimer = setTimeout(() => {
          this.refreshCasefile();
          this.refreshTimer = null;
        }, 250);
      }
    );

    // (Debug logging removed) -- previously had temporary store/error watchers

    // Do not preload tab content or counts here. All tab components and their data
    // will be lazy-loaded when the user activates the tab. This keeps initial
    // page load minimal.
    // Small runtime patch: ensure TransferClerk (lazy component) doesn't cause
    // null access when child code reads properties of caseState.casefile that
    // may be null during rapid UI updates. We attach a small shim to its
    // close handler if present once the component is available.
    this.$nextTick(() => {
      try {
        const checkTransfer = () => {
          const comp = this.$refs.transferClerkRef;
        if (!comp || !comp.$el) {
            // not ready yet, try later
            if (!this.transferClerkPatched) setTimeout(checkTransfer, 200);
            return;
          }
          // If the component exposes closeTransferClerkDialog method, wrap it
          if (comp.closeTransferClerkDialog && !this.transferClerkPatched) {
            this.origTransferClerkClose = comp.closeTransferClerkDialog.bind(comp);
            const self = this;
            comp.closeTransferClerkDialog = function () {
              try {
                // defensive guard: ensure editedCasefile exists before referencing
                if (!comp.editedCasefile) comp.editedCasefile = Object.assign({}, self.casefile || {});
              } catch (e) {
                // ignore
              }
               try {
                 // defensive: no-op logging removed
               } catch (e) { /* intentionally empty */ }
              // call original
              try { self.origTransferClerkClose && self.origTransferClerkClose(); } catch (e) { void 0; }
            };
            // Wrap transferClerkSubmit to defensively ensure editedCasefile fields exist
            try {
              if (comp.transferClerkSubmit && !comp.transferClerkSubmit.__patchedByDetailFast) {
                const origSubmit = comp.transferClerkSubmit.bind(comp);
                comp.transferClerkSubmit = function () {
                  try {
                    // ensure editedCasefile exists
                    if (!comp.editedCasefile) comp.editedCasefile = Object.assign({}, self.casefile || {});
                    // ensure id exists
                    if (!comp.editedCasefile.id && self.casefile && self.casefile.id) comp.editedCasefile.id = self.casefile.id;
                    // ensure branch id is present: prefer editedCasefile.branch, fall back to parent's casefile.branch
                    if (!comp.editedCasefile.branch) {
                      if (self.casefile && self.casefile.branch) comp.editedCasefile.branch = self.casefile.branch;
                      else if (comp.branch && comp.branch.brCode) {
                        const branches = self.$store.state.referenceData.branches || [];
                        const found = branches.find((b) => b && String(b.brCode) === String(comp.branch.brCode));
                        if (found && found.id) comp.editedCasefile.branch = found.id;
                      }
                    }
                   } catch (e) {
                     // defensive: prep failed; continue without logging
                   }
                  // call original submit and handle promise rejections to avoid uncaught errors
                  try {
                    const result = origSubmit();
                    if (result && typeof result.then === "function") {
                      return result.catch((err) => {
                        try {
                          this.$store && this.$store.dispatch && this.$store.dispatch("setSnackbar", { showing: true, color: "error", text: (err && err.message) || "Transfer failed" });
                        } catch (ee) { /* intentionally empty */ }
                        // eslint-disable-next-line no-console
                        console.error("detailFast: transferClerkSubmit promise rejection", err);
                        return undefined;
                      });
                    }
                    return result;
                  } catch (err) {
                    try {
                      this.$store && this.$store.dispatch && this.$store.dispatch("setSnackbar", { showing: true, color: "error", text: (err && err.message) || "Transfer failed" });
                    } catch (ee) { /* intentionally empty */ }
                    // eslint-disable-next-line no-console
                    console.error("detailFast: transferClerkSubmit call threw", err);
                    return undefined;
                  }
                };
                comp.transferClerkSubmit.__patchedByDetailFast = true;
              }
            } catch (e) {
              /* intentionally empty */
            }
            this.transferClerkPatched = true;
          }
        };
        checkTransfer();
      } catch (e) {
        // ignore
      }
    });
  },
  beforeDestroy() {
    if (this.unwatchCaseStatus) {
      try {
        this.unwatchCaseStatus();
      } catch (e) {
        /* intentionally empty */
      }
    }
    try {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
    } catch (e) { void 0; }
  },
};
</script>

<style scoped>
.pa-4{padding:16px}

.header-section {
  background: linear-gradient(to right, #1976d2, #1565c0) !important;
  position: relative;
  color: white !important;
}
.header-section .text-h5 { color: white !important; }
.status-chip { font-weight: 600; color: white !important; }
.flag-icon { transform: scale(1.2); }

.workflow-actions { background: #fff; border-radius: 8px; }
.workflow-title { border-bottom: 1px solid #e0e0e0; }
.title-text { font-size: 1.5rem; font-weight: 500; color: #37474f; }
.action-buttons { background: linear-gradient(to right, #f8f9fa, #ffffff); }

</style>

<style scoped>
/* Action wrapper styles copied from legacy view for consistent layout */
.action-wrapper {
  flex: 0 0 auto;
  width: calc(25% - 16px);
  margin: 8px;
  min-width: 250px;
}

@media (max-width: 1264px) {
  .action-wrapper {
    width: calc(33.33% - 16px);
  }
}

@media (max-width: 960px) {
  .action-wrapper {
    width: calc(50% - 16px);
  }
}

@media (max-width: 600px) {
  .action-wrapper {
    width: 100%;
    margin: 8px 0;
  }
}

/* Ensure tab skeleton wrapper reserves space so loader is visible while loading */
.tab-skeleton-wrapper { display: block; }
.tab-skeleton { padding: 12px 16px; }
</style>
